import Backbone from "backbone";
import _ from "underscore";
import { getCurrentVehicle } from "../utils/device";
import { UserService } from "../utils/users";
import { SubscriptionsService } from "../services/base";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import * as logger from "../utils/logger";
import Api from "./api";
import NotificationCenter from "./notificationCenter";
import { CommandHandler, ICommand} from "../utils/commandHandler";
import TranslationService from "./translation";
import { DriveStore } from "../store/reducers";
import { ErrorHandler } from "../utils/error.impl";
import { UserSync } from "../sync/users";
import { SecurityService } from "../utils/security";
import { IFeatureFlagsService } from "./featureFlags";
import { FeatureFlagName } from "../utils/featureFlagName";
import { IBackground } from "../store/mobile/types";
import { getServerDate } from "../utils/storage/date";
import constants from "../constants";

const METHOD_VERBS = ["Set", "Add", "Remove"];
const TEXT_MESSAGE_CHANGE_EVENT = `textmessage:change`;
const METHOD_SET = "Set";

export interface IVehicleDrivingEvent {
    isDriving: boolean;
    speed: number;
    dateTime: string;
}

export default class ServerEvents extends SubscriptionsService {
    private readonly events: Backbone.IEvents;
    private readonly database: string | null;
    private readonly dispatcher = dispatcher;
    private readonly logger = logger;
    private readonly api: Api;
    private readonly notificationCenter: NotificationCenter;
    private readonly user: string | null;
    private readonly userSync: UserSync;
    private readonly commandHandler?: CommandHandler;
    private lastRefreshCurrentUser = new Date(0);

    constructor(
        private readonly store: DriveStore,
        api: Api,
        notificationCenter: NotificationCenter,
        user: string | null,
        private readonly errorHandler: ErrorHandler,
        private readonly userService: UserService,
        private readonly securityService: SecurityService,
        private readonly featureFlagsService: IFeatureFlagsService,
        translator: TranslationService,
        mockDispatcher?: any,
        mockLogger?: any
    ) {
        super();
        this.api = api;
        this.notificationCenter = notificationCenter;
        this.user = user;
        this.userSync = new UserSync(this.userService, this.securityService, this.api, this.store);
        if (mockDispatcher) {
            this.dispatcher = mockDispatcher;
        }

        if (mockLogger) {
            this.logger = mockLogger;
        }

        this.events = _.clone(Backbone.Events);
        this.database = this.getDatabaseName();
        if (this.user && this.database) {
            this.commandHandler = new CommandHandler(this.store, this.user, this.errorHandler, translator);
        }
    }

    on(topic: string, callback: any) {
        this.events.off(topic, undefined); // Clear any existing event listeners for this
        this.events.on(topic, callback);
    }

    off(topic: string, callback?: any) {
        this.events.off(topic, callback);
    }

    startSubscriptions(): void {
        if (!this.database) {
            this.logger.log(this.logger.Source.SERVICES, `No database name selected. ServerEvents won't be initialized`, this.user || `'anonymous'`);
            return;
        }
        this.events.listenTo(this.dispatcher, DispatcherEvents.VehicleSelected, (vehicle: IIdEntity, oldVehicle: IIdEntity) =>
            this.onVehicleSelect(vehicle, oldVehicle));
        this.events.listenTo(this.dispatcher, DispatcherEvents.StatusOnline, (online: boolean) => {
            if (online && this.lastRefreshCurrentUser.getTime() + constants.TIME_BETWEEN_REFRESH_CURRENT_USER < getServerDate().getTime()) {
                this.refreshCurrentUser(true);
            }
        });
        this.events.listenTo(this.dispatcher, DispatcherEvents.StatusBackground, (background: IBackground) => {
            if (!background.isBackground &&
                this.lastRefreshCurrentUser.getTime() + constants.TIME_BETWEEN_REFRESH_CURRENT_USER < getServerDate().getTime()
            ) {
                this.refreshCurrentUser(true);
            }
        });
        this.subscribeToChannelTopics();
    }

    /**
     * Re-subscribes this service's WebSocket topics after the channel has been rebuilt.
     *
     * `WebSocketChannel.startSubscriptions()` discards the CometChannel these topics were
     * registered on, so without this the socket reconnects but no notifications arrive.
     * Deliberately excludes the `dispatcher` listeners set up in `startSubscriptions()` —
     * those are bound to the dispatcher, not the channel, and survive a reconnect.
     * Re-attaching them would double-fire every VehicleSelected/StatusOnline event.
     * See MYG-143588.
     */
    reconnectSubscriptions(): void {
        if (!this.database) {
            return;
        }
        this.subscribeToChannelTopics();
    }

    stopSubscriptions(): void {
        this.events.stopListening();
    }

    refreshCurrentUser(userDataOnly = false): void {
        const userId = this.api.getUser()?.id;
        if (userId) {
            userDataOnly ? this.userSync.fetchUser(userId, false, true) : this.userSync.fetchUser(userId);
            this.lastRefreshCurrentUser = getServerDate();
            this.logger.log(this.logger.Source.SYNC, `User data with id ${ userId } has been refreshed`, this.user);
        }
    }

    /**
     * Registers every WebSocket topic this service listens on. Safe to call again after a
     * reconnect: `WebSocketChannel.subscribe()` skips topics already present on the channel,
     * and after a rebuild the new channel carries none of them.
     */
    private subscribeToChannelTopics(): void {
        this.onVehicleSelect(getCurrentVehicle(this.store));
        this.onUserSelect(this.user ? this.userService.getUserByName(this.user) : null);
        this.setupDvirLogSubscriptionsUser();
        this.setupDutyStatusLogSubscriptions();
        this.setupShipmentLogSubscriptions();
        this.setupUserHosRuleSetSubscriptions();
        this.setupDriveCommandSubscriptions();
        this.setupUserInfoSubscriptions();
    }

    private getDatabaseName(): string | null {
        const credentials = this.api.getCurrentCredentials();
        return credentials && credentials.credentials.database || null;
    }

    private getVehicleMoveTopicName(vehicle: IIdEntity): string {
        return `vehiclemove|${ this.database }|${ vehicle.id }`;
    }

    private onVehicleMove(e: any) {
        this.events.trigger("vehicledriving:change", e);
    }

    private onVehicleSelect(vehicle: IIdEntity | null, oldVehicle?: IIdEntity | null) {
        if (vehicle && vehicle.id === "NoDeviceId") {
            this.unsubscribeFromEvents();
            return;
        }

        if (!vehicle || !oldVehicle || oldVehicle.id !== vehicle.id) {
            this.unsubscribeFromEvents();
            this.setupSubscriptionsForVehicles(vehicle);
        }
    }

    private onUserSelect(user: DeepReadonly<IUser> | null) {
        if (user && user.id === "NoUserId") {
            this.unsubscribeFromUserEvents();
            return;
        }

        if (user) {
            METHOD_VERBS.filter(v => v !== "Remove").forEach(verb => {
                this.notificationCenter.on(`dataupdate|${ this.database }|TextMessage|${ verb }|{recipient:{id:"${ user.id }"}}`, this.events.trigger.bind(this.events, TEXT_MESSAGE_CHANGE_EVENT));
            });
            this.notificationCenter.on(`dataupdate|${ this.database }|TextMessage|${ METHOD_SET }|{user:{id:"${ user.id }"}}`, this.events.trigger.bind(this.events, TEXT_MESSAGE_CHANGE_EVENT));
        }
    }

    private unsubscribeFromUserEvents() {
        if (this.featureFlagsService.hasFeatureFlag(FeatureFlagName.ENABLE_BATCH_UNSUBSCRIBE)) {
            const topics = [
                ...METHOD_VERBS.filter(v => v !== "Remove").map(verb =>
                    `dataupdate|${ this.database }|TextMessage|${ verb }|{recipient:*`),
                `dataupdate|${ this.database }|TextMessage|${ METHOD_SET }|{user:*`
            ];
            this.notificationCenter.offMultiple(topics);
        } else {
            METHOD_VERBS.filter(v => v !== "Remove").forEach(verb => {
                this.notificationCenter.off(`dataupdate|${ this.database }|TextMessage|${ verb }|{recipient:*`);
            });
            this.notificationCenter.off(`dataupdate|${ this.database }|TextMessage|${ METHOD_SET }|{user:*`);
        }
    }

    private unsubscribeFromEvents() {
        if (this.featureFlagsService.hasFeatureFlag(FeatureFlagName.ENABLE_BATCH_UNSUBSCRIBE)) {
            const topics = [
                `vehiclemove|*`,
                ...METHOD_VERBS.filter(v => v !== "Remove").map(verb =>
                    `dataupdate|${ this.database }|TextMessage|${ verb }|{device:*`),
                ...METHOD_VERBS.map(verb =>
                    `dataupdate|${ this.database }|DVIRLog|${ verb }|{device*`),
                ...METHOD_VERBS.map(verb =>
                    `dataupdate|${ this.database }|shipmentlog|${ verb }|{device*`)
            ];
            this.notificationCenter.offMultiple(topics);
        } else {
            this.notificationCenter.off(`vehiclemove|*`);
            METHOD_VERBS.filter(v => v !== "Remove").forEach(verb => {
                this.notificationCenter.off(`dataupdate|${ this.database }|TextMessage|${ verb }|{device:*`);
            });
            METHOD_VERBS.forEach(verb => {
                this.notificationCenter.off(`dataupdate|${ this.database }|DVIRLog|${ verb }|{device*`);
            });
            METHOD_VERBS.forEach(verb => {
                this.notificationCenter.off(`dataupdate|${ this.database }|shipmentlog|${ verb }|{device*`);
            });
        }
    }

    private setupSubscriptionsForVehicles(vehicle: IIdEntity | null) {
        this.setupVehicleMove(vehicle);
        vehicle && this.setupVehicleTextMessageSubscriptions(vehicle);
        vehicle && this.setupDvirLogSubscriptionsVehicle(vehicle);
        vehicle && this.setupTrailerAttachmentSubscriptions(vehicle);
        vehicle && this.setupShipmentLogSubscriptionsForVehicle(vehicle);
    }

    private setupVehicleMove(vehicle: IIdEntity | null) {
        const topicName = this.getVehicleMoveTopicName(vehicle || { id: "xyz" });

        if (!this.notificationCenter.subscribed(topicName)) {
            vehicle && this.notificationCenter.on(topicName, e => this.onVehicleMove(e));
        }
    }

    private setupVehicleTextMessageSubscriptions(vehicle: IIdEntity) {
        const currentDeviceId = vehicle.id;

        METHOD_VERBS.filter(v => v !== "Remove").forEach(verb => {
            this.notificationCenter.on(`dataupdate|${ this.database }|TextMessage|${ verb }|{device:{id:"${ currentDeviceId }"}}`, this.events.trigger.bind(this.events, TEXT_MESSAGE_CHANGE_EVENT));
        });

    }

    private onDutyStatusLogUpdate(e: any) {
        this.events.trigger("dutystatuslog:change", e);
    }

    private onUserHosRuleSetUpdate(e: any) {
        this.events.trigger("userhosruleset:change", e);
    }

    private setupDutyStatusLogSubscriptions() {
        this.userService.getUsers(true).forEach(user => {
            METHOD_VERBS.forEach(verb => {
                this.notificationCenter.on(`dataupdate|${ this.database }|DutyStatusLog|${ verb }|{driver:{id:"${ user.id }"}}`, e => this.onDutyStatusLogUpdate({ e, userName: user.name }));
            });
        });
    }

    private setupDvirLogSubscriptionsVehicle(vehicle: IIdEntity) {
        const currentDeviceId = vehicle.id;
        const dvirLogChangeEvent = `dvirlog:change`;

        METHOD_VERBS.forEach(verb => {
            this.notificationCenter.on(`dataupdate|${ this.database }|DVIRLog|${ verb }|{device:{id:"${ currentDeviceId }"}}`, e => this.events.trigger(dvirLogChangeEvent, e));
        });
    }

    private setupDvirLogSubscriptionsUser() {
        const dvirLogChangeEvent = `dvirlog:change`;
        this.userService.getUsers(true).forEach(user => {
            METHOD_VERBS.forEach(verb => {
                this.notificationCenter.on(`dataupdate|${ this.database }|DVIRLog|${ verb }|{driver:{id:"${ user.id }"}}`, e => this.events.trigger(dvirLogChangeEvent, e));
            });
        });
    }

    private setupUserHosRuleSetSubscriptions() {
        this.userService.getUsers(true).forEach(user => {
            METHOD_VERBS.forEach(verb => {
                this.notificationCenter.on(`dataupdate|${ this.database }|UserHosRuleSet|${ verb }|{driver:{id:"${ user.id }"}}`, e => this.onUserHosRuleSetUpdate({ e, userName: user.name }));
            });
        });
    }

    private onShipmentLogUpdate(e: any) {
        this.events.trigger("shipmentlog:change", e);
    }

    private setupShipmentLogSubscriptions() {
        this.userService.getUsers(true).forEach(user => {
            METHOD_VERBS.forEach(verb => {
                this.notificationCenter.on(`dataupdate|${ this.database }|shipmentlog|${ verb }|{driver:{id:"${ user.id }"}}`, e => this.onShipmentLogUpdate(e));
            });
        });
    }

    private setupShipmentLogSubscriptionsForVehicle(vehicle: IIdEntity) {
        METHOD_VERBS.forEach(verb => {
            this.notificationCenter.on(`dataupdate|${ this.database }|shipmentlog|${ verb }|{device:{id:"${ vehicle.id }"}}`, e => this.onShipmentLogUpdate(e));
        });
    }

    private setupTrailerAttachmentSubscriptions(vehicle: IIdEntity) {
        const currentDeviceId = vehicle.id;
        const trailerAttachmentChangeEvent = "trailerattachment:change";

        METHOD_VERBS.filter(v => v !== "Remove").forEach(verb => {
            this.notificationCenter.on(`dataupdate|${ this.database }|trailerattachment|${ verb }|{device:{id:"${ currentDeviceId }"}}`, e => this.events.trigger(trailerAttachmentChangeEvent, e));
        });
    }

    private onDriveCommand(command: ICommand) {
        if (!this.commandHandler) {
            this.logger.error(this.logger.Source.API, `Command sent ${ command && command.name } but no commandHandler available`);
            return;
        }
        this.commandHandler.handle(command);
    }

    private setupDriveCommandSubscriptions() {
        this.user && this.notificationCenter.on(`drive-command|${ this.database }|{driver:{id:"${ this.userService.getUserByName(this.user)?.id }"}}`, this.onDriveCommand.bind(this));
        this.user && this.notificationCenter.on(`drive-command|${ this.database }`, this.onDriveCommand.bind(this));
    }

    private setupUserInfoSubscriptions() {
        this.userService.getUsers().forEach(user => {
            const topic = `dataupdate|${ this.database }|User|${ METHOD_SET }|{user:{id:"${ user.id }"}}`;
            this.logger.log(this.logger.Source.SYNC, `Subscribing to User updates with topic: ${ topic }`, this.user);
            this.notificationCenter.on(topic, e => this.onUserInfoUpdate(e));
        });
    }

    private onUserInfoUpdate(e: {data: string[]}) {
        let driverId = this.userService.getUserById(e.data[0])?.id;
        driverId && this.userSync.syncUserDebounced(driverId);
    }
}
